/*
 * Sourced from https://v0.dev/chat/menu-navbar-sidebar-w-o-localization-Y0lZuopJCUv
 * See docs/v0.md for more
 */

'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'

import { ChevronDownIcon, User } from 'lucide-react'

import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-mobile'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useSidebar } from '@/components/ui/sidebar'

export interface MenuItem {
    title: string
    shortTitle?: string
    url: string
    description?: string
    icon?: React.ReactNode
    items?: MenuItem[]
}

export interface NavbarLogo {
    url: string
    src: string
    alt: string
    width?: number
    height?: number
}

export interface NavbarAuth {
    login: {
        title: string
        url: string
    }
    signup: {
        title: string
        url: string
    }
    myTrips: {
        title: string
        url: string
    }
    account: {
        title: string
        url: string
    }
    points: {
        title: string
        url: string
    }
}

export interface NavbarProps {
    logo: NavbarLogo
    menu: MenuItem[]
    auth?: NavbarAuth
}

const navLinkStyle =
    'inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1'

export const Navbar = ({ logo, menu, auth }: NavbarProps) => {
    const [isLoggedIn, setIsLoggedIn] = useState(false)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const { toggleSidebar, setOpenMobile } = useSidebar()
    const isMobile = useIsMobile()
    const timeoutRef = useRef<NodeJS.Timeout>(null)

    const toggleLogin = () => {
        setIsLoggedIn(!isLoggedIn)
    }

    // Close sidebar when switching from mobile to desktop
    useEffect(() => {
        if (!isMobile) {
            setOpenMobile(false)
        }
    }, [isMobile, setOpenMobile])

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }
        setIsDropdownOpen(true)
    }

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsDropdownOpen(false)
        }, 150)
    }

    return (
        <header className="sticky top-0 z-30 w-full border-b bg-background lg:static">
            <div className="mx-auto flex h-[50px] w-full items-center justify-between px-5 lg:h-[70px] lg:w-[980px] lg:px-0">
                {/* Left side: Logo and Navigation */}
                <div className="flex items-center">
                    {/* Logo */}
                    <a
                        href={logo.url}
                        className="mr-4 flex items-center rounded-md focus:outline-none"
                    >
                        <Image
                            priority
                            src={logo.src}
                            alt={logo.alt}
                            width={logo.width}
                            height={logo.height}
                            className="h-8 w-auto"
                        />
                    </a>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex" aria-label="Main navigation">
                        <ul className="flex list-none items-center gap-1">
                            {menu.map((item) =>
                                item.items ? (
                                    <li key={item.title} className="group/nav-item relative">
                                        <a href={item.url} className={cn(navLinkStyle, 'gap-1')}>
                                            {item.title}
                                            <ChevronDownIcon
                                                className="relative top-[1px] ml-1 size-3 transition duration-300 group-hover/nav-item:rotate-180"
                                                aria-hidden="true"
                                            />
                                        </a>
                                        <div className="invisible absolute top-full left-0 z-50 mt-1.5 w-72 rounded-md border bg-popover p-4 text-popover-foreground opacity-0 shadow transition-all duration-200 group-focus-within/nav-item:visible group-focus-within/nav-item:opacity-100 group-hover/nav-item:visible group-hover/nav-item:opacity-100 before:absolute before:-top-2 before:left-0 before:h-2 before:w-full before:content-['']">
                                            <div className="grid gap-1">
                                                {item.items.map((subItem) => (
                                                    <a
                                                        key={subItem.url}
                                                        href={subItem.url}
                                                        className="block rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
                                                    >
                                                        {subItem.title}
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    </li>
                                ) : (
                                    <li key={item.title}>
                                        <a href={item.url} className={navLinkStyle}>
                                            {item.title}
                                        </a>
                                    </li>
                                ),
                            )}
                        </ul>
                    </nav>
                </div>

                {/* Right side: User Menu */}
                {auth && (
                    <div className="hidden items-center lg:flex">
                        <DropdownMenu
                            modal={false}
                            open={isDropdownOpen}
                            onOpenChange={setIsDropdownOpen}
                        >
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    className="flex items-center gap-1 text-sm font-medium text-black hover:bg-transparent"
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    {isLoggedIn ? (
                                        <>
                                            <User className="h-4 w-4" />
                                            <span>My Account</span>
                                        </>
                                    ) : (
                                        <span>Log In</span>
                                    )}
                                    <ChevronDownIcon
                                        className="relative top-[1px] ml-1 size-3 transition duration-300 group-data-[state=open]:rotate-180"
                                        aria-hidden="true"
                                    />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                className="w-64"
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                            >
                                <div className="p-4">
                                    <div className="mb-4 space-y-2">
                                        {!isLoggedIn && (
                                            <Button
                                                asChild
                                                className="w-full bg-[#007380] transition-colors duration-200 hover:bg-[#007380]/80"
                                                size="lg"
                                            >
                                                <a href={auth.signup.url}>{auth.signup.title}</a>
                                            </Button>
                                        )}
                                        <Button
                                            onClick={toggleLogin}
                                            variant="outline"
                                            className="w-full bg-transparent"
                                            size="lg"
                                        >
                                            <a href={auth.login.url}>{auth.login.title}</a>
                                        </Button>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem asChild>
                                            <a href={auth.myTrips.url} className="cursor-pointer">
                                                {auth.myTrips.title}
                                            </a>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <a href={auth.account.url} className="cursor-pointer">
                                                {auth.account.title}
                                            </a>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <a href={auth.points.url} className="cursor-pointer">
                                                {auth.points.title}
                                            </a>
                                        </DropdownMenuItem>
                                    </div>
                                </div>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                )}

                {/* Mobile Menu Button - Hamburger icon with larger size */}
                <div className="flex items-center lg:hidden">
                    <button
                        type="button"
                        className="flex h-5 w-[30px] flex-col justify-between"
                        onClick={toggleSidebar}
                        aria-label="Toggle Sidebar"
                    >
                        <span className="h-[3px] w-full rounded-full bg-[#2e3033]" />
                        <span className="h-[3px] w-full rounded-full bg-[#2e3033]" />
                        <span className="h-[3px] w-full rounded-full bg-[#2e3033]" />
                    </button>
                </div>
            </div>
        </header>
    )
}
